import { usePathname, useRouter } from 'next/navigation'
import { ChangeEventHandler, FormEvent, useEffect, useState } from 'react'

import { useHeaderSearch } from '@/entities/header-search'

import { useCurrentPath } from './use-current-path'

const SEARCH_QUERY_COOKIE_MAX_AGE = 300 // 5 minutes

export const useSearchForm = () => {
	const router = useRouter()
	const pathname = usePathname()
	const { withSalt } = useCurrentPath()
	const IS_SEARCH_PAGE = pathname.includes('catalog/products') || pathname.includes('catalog/category')

	const [value, setValue] = useState<string>('')
	const [debouncedValue, setDebouncedValue] = useState(value)
	const searchValue = debouncedValue.length > 3 ? debouncedValue : ''
	const { isLoading, redirectUrl } = useHeaderSearch(searchValue)

	const handleChange: ChangeEventHandler<HTMLInputElement> = async event => {
		const { value } = event.target
		setValue(value.toLowerCase())
	}
	const resetValue = () => setValue('')

	const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		if (isLoading || !value.trim()) return

		// Сохраняем исходный запрос в cookie для случая редиректа с несуществующей услуги
		if (typeof document !== 'undefined') {
			document.cookie = `LAST_SEARCH_QUERY=${encodeURIComponent(
				value.toLowerCase()
			)}; path=/; max-age=${SEARCH_QUERY_COOKIE_MAX_AGE}; SameSite=Lax`
		}

		const currentPath = pathname
		const isOnServicePage = currentPath.includes('/service/product/')

		if (redirectUrl) {
			// Если уже на целевой странице услуги, не делаем повторный переход
			const targetPath = redirectUrl.split('?')[0]
			if (currentPath === targetPath) return

			await router.push(redirectUrl)
		} else if (!isOnServicePage) {
			// Переходим на каталог только если не на странице услуги
			await router.push(`/catalog/products/${encodeURIComponent(value)}?${withSalt}`)
		}
	}
	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value.toLowerCase())
		}, 300)

		return () => {
			clearTimeout(handler)
		}
	}, [value])

	useEffect(() => {
		if (!IS_SEARCH_PAGE) setValue('')
	}, [IS_SEARCH_PAGE]) // eslint-disable-line react-hooks/exhaustive-deps

	return {
		value: value.toLowerCase(),
		onChange: handleChange,
		reset: resetValue,
		onSubmit: onSubmit,
		dValue: debouncedValue
	}
}
